import axios from 'axios'
import smCrypto from 'sm-crypto'
import envList from '../envList.json'

export const AUTH_STORAGE_KEY = 'servoControlAuth'
export const REFERENCE_TOKEN_KEY = 'token'
export const AUTH_EXPIRED_EVENT = 'servo-control:auth-expired'
export const AUTH_ENVIRONMENT_MISMATCH_EVENT = 'servo-control:auth-environment-mismatch'

function getAuthBaseUrl(managerUrl) {
  const configuredBaseUrl = process.env.VUE_APP_LOGIN_API_BASE_URL
  if (configuredBaseUrl) return configuredBaseUrl.replace(/\/$/, '')

  const resolvedManagerUrl = managerUrl
    || localStorage.getItem('selectedEnvManagerUrl')
    || envList[0]?.manager_url
  if (!resolvedManagerUrl) throw new Error('认证服务地址未配置')
  return `${resolvedManagerUrl.replace(/\/$/, '')}/xiaozhi`
}

function getAuthorizationHeader(session) {
  const token = session?.token || session?.accessToken || session?.access_token || session?.authorization
  if (!token) return ''
  return /^Bearer\s/i.test(token) ? token : `Bearer ${token}`
}

async function fetchMerchantName(user, authorization, managerUrl) {
  if (!user?.id || !user?.tenantId) return ''

  const response = await authClient.get(`${getAuthBaseUrl(managerUrl)}/users`, {
    params: {
      page: 1,
      limit: 10,
      keyword: String(user.id)
    },
    headers: { Authorization: authorization }
  })

  if (response.data?.code !== 0) return ''

  const users = response.data?.data?.list || []
  const currentUser = users.find(item => String(item.userid) === String(user.id))
  return currentUser?.tenantName || ''
}

const authClient = axios.create({
  timeout: 15000
})

function hasAuthorizationHeader(config) {
  const headers = config?.headers
  return Boolean(
    headers?.get?.('Authorization')
    || headers?.Authorization
    || headers?.authorization
  )
}

function handleUnauthorizedResponse(config, status, data) {
  if (
    !hasAuthorizationHeader(config)
    || !isUnauthorizedResponse(status, data)
  ) {
    return
  }

  if (isLoginEnvironmentMismatch(config?.url)) {
    try {
      assertLoginEnvironment(config.url)
    } catch {
      // 跨环境访问只提示并保留登录态。
    }
    return
  }

  expireAuthSession()
}

authClient.interceptors.response.use(
  (response) => {
    handleUnauthorizedResponse(response.config, response.status, response.data)
    return response
  },
  (error) => {
    handleUnauthorizedResponse(
      error.config,
      error.response?.status,
      error.response?.data
    )
    return Promise.reject(error)
  }
)

function createCaptchaId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16)
    const value = char === 'x' ? random : ((random & 0x3) | 0x8)
    return value.toString(16)
  })
}

export async function fetchPublicConfig() {
  const response = await authClient.get(`${getAuthBaseUrl()}/user/pub-config`)
  if (response.data?.code !== 0 || !response.data?.data?.sm2PublicKey) {
    throw new Error(response.data?.msg || '登录配置加载失败')
  }
  return response.data.data
}

export async function fetchCaptcha() {
  const captchaId = createCaptchaId()
  const response = await authClient.get(`${getAuthBaseUrl()}/user/captcha`, {
    params: { uuid: captchaId },
    responseType: 'blob',
    headers: {
      Pragma: 'no-cache',
      'Cache-Control': 'no-cache'
    }
  })

  return {
    captchaId,
    imageUrl: URL.createObjectURL(response.data)
  }
}

export async function loginWithPassword({ username, password, captcha, captchaId, publicKey }) {
  const encryptedPassword = `04${smCrypto.sm2.doEncrypt(`${captcha}${password}`, publicKey, 1)}`
  const response = await authClient.post(`${getAuthBaseUrl()}/user/login`, {
    username,
    password: encryptedPassword,
    captchaId
  })

  if (response.data?.code !== 0 || !response.data?.data) {
    throw new Error(response.data?.msg || '登录失败')
  }

  return response.data.data
}

export async function fetchCurrentUser(session = loadAuthSession()) {
  const authorization = getAuthorizationHeader(session)
  if (!authorization) {
    expireAuthSession()
    throw new Error('登录信息已失效，请重新登录')
  }

  const loginManagerUrl = session?.loginEnvironmentUrl
  const response = await authClient.get(`${getAuthBaseUrl(loginManagerUrl)}/user/info`, {
    headers: { Authorization: authorization }
  })

  if (response.data?.code !== 0 || !response.data?.data) {
    throw new Error(response.data?.msg || '用户信息加载失败')
  }

  const user = response.data.data
  if (!user.tenantName && !user.tenant?.name && user.tenantId) {
    try {
      user.tenantName = await fetchMerchantName(user, authorization, loginManagerUrl)
    } catch (error) {
      // 用户基础信息仍可正常展示；商户名称不可用时由页面显示占位符。
      user.tenantName = ''
    }
  }

  return user
}

export function buildAvatarPreviewUrl(user) {
  if (!user?.id || !user?.avatarUrl) return ''

  const avatarVersion = encodeURIComponent(user.avatarUrl)
  const loginManagerUrl = loadAuthSession()?.loginEnvironmentUrl
  return `${getAuthBaseUrl(loginManagerUrl)}/user/avatar/preview/${user.id}?t=${avatarVersion}`
}

export function saveAuthSession(session) {
  const serialized = JSON.stringify(session)
  localStorage.setItem(AUTH_STORAGE_KEY, serialized)
  // 与参考管理端保持相同 token 存储格式，便于现有请求拦截器复用。
  localStorage.setItem(REFERENCE_TOKEN_KEY, serialized)
}

export function loadAuthSession() {
  const serialized = localStorage.getItem(AUTH_STORAGE_KEY)
  if (!serialized) return null

  try {
    return JSON.parse(serialized)
  } catch (error) {
    clearAuthSession()
    return null
  }
}

export function clearAuthSession() {
  localStorage.removeItem(AUTH_STORAGE_KEY)
  localStorage.removeItem(REFERENCE_TOKEN_KEY)
}

export function isUnauthorizedResponse(status, data) {
  return Number(status) === 401 || Number(data?.code) === 401
}

function normalizeServerUrl(url) {
  if (!url) return ''

  try {
    return new URL(url).origin.toLowerCase()
  } catch (error) {
    return String(url).replace(/\/+$/, '').toLowerCase()
  }
}

function getEnvironmentDisplayName(session) {
  const loginManagerUrl = session?.loginEnvironmentUrl
  const environment = envList.find(
    item => normalizeServerUrl(item.manager_url) === normalizeServerUrl(loginManagerUrl)
  )
  const name = environment?.name || session?.loginEnvironmentName || '当前环境'
  return name.replace(/环境$/, '服')
}

export function isLoginEnvironmentMismatch(targetUrl, session = loadAuthSession()) {
  const loginManagerUrl = session?.loginEnvironmentUrl
  if (!loginManagerUrl || !targetUrl) return false
  return normalizeServerUrl(loginManagerUrl) !== normalizeServerUrl(targetUrl)
}

export function assertLoginEnvironment(targetUrl, session = loadAuthSession()) {
  if (!isLoginEnvironmentMismatch(targetUrl, session)) return

  const message = `当前登录的是${getEnvironmentDisplayName(session)}，不可访问其他服务数据`
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_ENVIRONMENT_MISMATCH_EVENT, {
      detail: { message }
    }))
  }

  const error = new Error(message)
  error.code = 'AUTH_ENVIRONMENT_MISMATCH'
  throw error
}

export function expireAuthSession() {
  clearAuthSession()
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT))
  }
}
